import React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";

import useDragTransfer from "../../common/react-hooks/useDragTransfer";

import LegoAdminPageContext from "../../../pages/legoAdminPageContext";
import { SwitchInput } from "../../common/SwitchInput";
import SingleTextEditor from "../../common/editors/SingleTextEditor";
import { IconButton } from "../../common/IconButton";
import LegoEditButton from "../LegoEditButton";
import CountryFlag from "../../common/CountryFlag";
import BadgeColorHash from "../../common/BadgeColorHash";
import TrimSpec from "../trims/TrimSpec";
import StableUrlRedirectFinder from "./StableUrlRedirectFinder";
import BtnSeeDiff from "../../common/editors/BtnSeeDiff";
import { UserAvatarLive } from "../../common/UserAvatarLive";
import { LocaleFlag } from "../LocaleFlag";
import { fromNow } from "../../common/TimeUtils";

function isReactivation(change) {
  return change.doc.id === change.oldDoc?.id && change.doc.active !== change.oldDoc?.active;
}

// Returns suggestion data { url, key? } for a remove change, or null if no candidate found.
// Pure function — used both for rendering and for the "accept all" batch action.
function computeRemoveSuggestion(change, pendingChanges) {
  const { oldDoc } = change;

  // Try to find old fusebox id among the other uncommited changes
  const prevIds = _.filter(pendingChanges, c => c.changeType !== 'remove' && (c.doc?.country === oldDoc?.country || (c.doc?.country === 'global' && c.doc?.key !== oldDoc?.key)) && c.doc?.id === oldDoc.id);

  // Sort by being in the same key first, then by starting with the same year
  const prevId = _.sortBy(prevIds, c => c.doc?.key !== oldDoc.key, c => !c.doc.url.startsWith(oldDoc.url.slice(0, 5)))[0];

  if (!prevId) return null;

  const suggestion = { url: prevId.doc.url };
  if (prevId.doc.key !== oldDoc.key) {
    suggestion.key = prevId.doc.key;
  }
  return suggestion;
}

function renderSuggestionAndWarnings(change, pendingChanges, resolutions, onResolutionsChange) {
  const { changeType, oldDoc, _id } = change;

  if (changeType === 'remove') {
    // If this remove is auto-resolved it has not been used, don't offer a redirect suggestion
    if (resolutions[_id]?.notUsed) {
      return null;
    }

    const suggestion = computeRemoveSuggestion(change, pendingChanges);
    if (!suggestion) {
      return <div className="text-danger text-center">???</div>;
    }

    const acceptSuggestion = () => onResolutionsChange({ ...resolutions, [_id]: suggestion });

    let acceptBtn = null;
    if (!_.isEqual(resolutions[_id], suggestion)) {
      acceptBtn = <IconButton fill icon="check" className="mr-1" level="success" small onClick={acceptSuggestion}>Accept</IconButton>;
    }

    const differentYear = suggestion.url?.slice(0, 4) !== oldDoc.url?.slice(0, 4);

    return <div className={(acceptBtn ? "border border-success" : "") + ` ${differentYear ? 'bg-light-danger': ''} rounded text-ellipsis `} title={suggestion.url} style={{maxWidth: '320px'}}>
      {acceptBtn}
      &nbsp;&#x27F6; <span className="text-secondary text-center monospace">
        {suggestion.key ? <><TrimSpec spec="modelId" value={suggestion.key} />/</> : ''}
        {suggestion.url}</span>
    </div>;
  } else if (changeType === 'update') {
    if (isReactivation(change)) {
      return <span className="badge badge-success">Re-activation</span>;
    } else if (change.metadataChange) {
      return <span><span className="badge badge-light-secondary">Metadata</span> <BtnSeeDiff a={change.oldDoc.metadata} b={change.doc.metadata}>Diff</BtnSeeDiff></span>;
    } else {
      // Check if update is from an id that still exists
      const existingId = _.find(pendingChanges, c => c.doc?.id === oldDoc?.id);
      if (existingId) {
        return <>
          {(change.doc.active && !change.oldDoc.active) ? <span className="badge badge-success mr-1">Re-activation</span> : null}
          <span className="badge badge-danger">Overwritten ID still exists!</span>
        </>;
      }
      return <div className="text-warning text-center">-</div>;
    }
  }
  return null;
}



const RESOLUTION_DRAG_MIME = 'application/stable-url-resolution';

function ResolutionEditor({ resolution, onChange, change }) {
  const { page } = useContext(LegoAdminPageContext);
  const { changeType } = change;
  const { draggableProps, dropZoneProps, dropZoneStyle } = useDragTransfer(RESOLUTION_DRAG_MIME, { onDrop: onChange });

  const currentKey = (change.doc || change.oldDoc).key;
  const currentUrl = (change.doc || change.oldDoc).url;
  const findRedirectUrl = () => {
    // TODO: Find way of injecting urls that weren't added yet but will in this commit, and also the ones removed
    page.openModal(<StableUrlRedirectFinder
      locale={change.locale}
      initialUrl={resolution?.url || currentUrl || ''}
      initialKey={resolution?.key || currentKey || ''}
      countries={change.countries}
      type={'fusebox'}
      onSelect={({ key, url }) => {
        page.closeModal();
        if(key && key !== currentKey) {
          onChange({ key, url });
        } else {
          onChange({ url: url });
        }
      }}
    />, {title: <span>
      Redirect <TrimSpec spec="modelId" value={currentKey} /> url "<span className="monospace text-info">{(change.doc || change.oldDoc).url}</span>"</span>});
  }

  if (changeType === 'remove') {
    const canDrag = resolution === true || resolution?.url || resolution?.notUsed;
    return <div className="d-flex align-items-center" {...dropZoneProps} style={dropZoneStyle}>
      {canDrag ? <IconButton icon="drag_indicator" small className="mr-1" title="Drag to copy resolution" {...draggableProps(resolution)} /> : null}
      {
        resolution !== true && !resolution?.notUsed ?
          <>
            {resolution?.key && resolution?.key !== (change.doc || change.oldDoc).key ? <TrimSpec style={{zoom: 0.8}} spec="modelId" value={resolution?.key} /> : null}
            &nbsp;<SingleTextEditor small placeholder="Redirect url..." value={resolution?.url || ''} onChange={(value) => onChange({ ... resolution, url: value.trim() })} />
            <IconButton icon="search" small onClick={findRedirectUrl} />
          </> : null}

      {resolution?.notUsed ? (
        <span className="">✔ new and not used yet</span>
      ) : (
        <SwitchInput value={resolution === true ? true : false} onChange={(value) => onChange(value)} className="ml-2">
          {resolution === true ? '💀 Delete without redirect' : <span className="translucent">💀&nbsp;</span>}
        </SwitchInput>
      )}
    </div>;
  } else if (changeType === 'update') {
    if (isReactivation(change) || change.metadataChange) {
      return <span className="">✔</span>;
    } else {
      return <SwitchInput value={resolution === true ? true : false} onChange={(value) => onChange(value)} />;
    }
  } else if (changeType === 'add') {
    return <span className="text-success text-center">-</span>;
  }
  return null;
}

function groupIdenticalChangesWithDifferentCountries(changes, ungroupedKeys) {
  // Group by entire object except for country
  const groupedChanges = _.groupBy(changes, ({ _id, doc, collapseOnCommit, oldDoc, createdAt, updatedAt, __v, ...c }) => { 
    const fieldsToOmit = ['_id', 'createdAt', 'updatedAt', '__v'];
    if(!ungroupedKeys.includes((doc || oldDoc).key)) {
      fieldsToOmit.push('country');
    }
    return JSON.stringify({ doc: _.omit(doc, fieldsToOmit), oldDoc: _.omit(oldDoc, fieldsToOmit), ...c });
  });

  return _.map(groupedChanges, (changes) => {
    return { ...changes[0], countries: _.map(changes, c => (c.doc || c.oldDoc).country), changes: changes };
  });
}

function expandResolutionsByCountries(changesWithCountries, resolutions) {
  let newResolutions = { ...resolutions };
  for (const change of changesWithCountries) {
    for (const originalChange of change.changes) {
      newResolutions[originalChange._id] = resolutions[change._id];
    }
  }
  return newResolutions;
}

function localeKey(change) {
  return change.locale + '_' + (change.doc || change.oldDoc).key;
}


export default function StableUrlsCommitEditor({ type }) {
  const { page } = useContext(LegoAdminPageContext);
  const [pendingChanges, setPendingChanges] = useState();
  const [resolutions, setResolutions] = useState({});
  const [highlightedId, setHighlightedId] = useState(null);
  const [checkedGroupKeys, setCheckedGroupKeys] = useState(new Set());
  const [ungroupedKeys, setUngroupedKeys] = useState([]);
  const [hideMetadataChanges, setHideMetadataChanges] = useState(false);
  const [visibleModelLimit, setVisibleModelLimit] = useState(10);

  const stableUrlsService = page.service('/services/stable-urls/changes');

  const fetchPendingChanges = async () => {
    const data = await stableUrlsService.find({ query: { type } });
    const groupedChanges = groupIdenticalChangesWithDifferentCountries(data, ungroupedKeys);
    setPendingChanges(groupedChanges);
  }


  const changeIsReady = (change) => {
    if (change.changeType === 'remove') {
      const resolution = resolutions[change._id];
      return resolution === true || resolution?.url || resolution?.notUsed === true;
    } else if (change.changeType === 'update') {
      if (isReactivation(change) || change.metadataChange) {
        return true;
      } else {
        return resolutions[change._id] === true;
      }
    } else if (change.changeType === 'add') {
      return true;
    }
  }

  const canCommit = (changes) => {
    const uniqueKey = c => (c.doc || c.oldDoc).country + ':' + (c.doc || c.oldDoc).key + ':' + (c.doc || c.oldDoc).url;
    const changesByCountryKeyUrl = _.groupBy(changes, uniqueKey)

    for (const change of changes) {
      const resolution = resolutions[change._id];
      if (change.changeType === 'remove') {
        if (!resolution || !(resolution === true || resolution.url || resolution.notUsed === true)) {
          return <><span className="badge badge-danger">REMOVE</span> operation <TrimSpec spec="modelId" value={change.oldDoc.key} /> <span className="monospace text-info">{change.oldDoc.url}</span> should be either confirmed or a redirect</>;
        }
      } else if (change.changeType === 'update') {
        if (!isReactivation(change) && !change.metadataChange) {
          if (resolution !== true) {
            return <><span className="badge badge-warning">UPDATE</span> operation <TrimSpec spec="modelId" value={change.doc.key} /> <span className="monospace text-info">{change.doc.url}</span> must be confirmed</>;
          }
        }
      }

      const url = (change.doc || change.oldDoc).url;
      if (changesByCountryKeyUrl[uniqueKey(change)]?.length > 1) {
        return <>URL "<span className="monospace text-info">{url}</span>" has collisions with ids: {changesByCountryKeyUrl[uniqueKey(change)].map(c => <LegoEditButton key={c._id} legoId={(c.doc || c.oldDoc).id}><BadgeColorHash id={(c.doc || c.oldDoc).id} /></LegoEditButton>)}</>;
      }
    }
  }

  function HighlightedId({ id }) {
    if (id) {
      return <span className={"monospace text-secondary p-1" + (highlightedId === id ? ' bg-highlight' : '')}
        onMouseEnter={() => setHighlightedId(id)}
        onMouseLeave={() => setHighlightedId(null)}>
        <LegoEditButton legoId={id}><BadgeColorHash id={id} /></LegoEditButton>
      </span>;
    }
    return <span className="monospace text-secondary p-1 small">-</span>;
  }

  const groupedChanges = useMemo(() => {
    const currentUserId = page.getLoggedUserSignature()?.id;
    const entries = Object.entries(_.groupBy(pendingChanges || [], localeKey));
    return _.sortBy(entries, ([groupKey, changes]) => {
      const hasCurrentUser = changes.some(change => change.createdBy?.id === currentUserId);
      if (hasCurrentUser) {
        // Current user groups first, sorted by key
        return [0, groupKey];
      } else {
        // Other groups after, sorted by user id + '_' + key
        const userId = changes[0].createdBy?.id || '';
        return [1, userId + '_' + groupKey];
      }
    });
  }, [pendingChanges, page]);

  // Initialize checked state when groupedChanges changes
  useEffect(() => {
    const currentUser = page.getLoggedUserSignature();
    const currentUserId = currentUser?.id;
    const newChecked = new Set();
    for (const [groupKey, keyGroupChanges] of groupedChanges) {
      // Check by default if all changes are from current user
      if (keyGroupChanges.every(change => change.createdBy?.id === currentUserId)) {
        newChecked.add(groupKey);
      }
    }
    setCheckedGroupKeys(newChecked);
  }, [groupedChanges, page]);

  // Auto-set notUsed resolution for remove changes that haven't been used and were created recently
  useEffect(() => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    setResolutions(prev => {
      const newResolutions = { ...prev };

      for (const change of (pendingChanges || [])) {
        if (change.changeType === 'remove' && change.oldDoc) {
          const usage = change.oldDoc.usage;
          const oldDocCreatedAt = change.oldDoc.createdAt ? new Date(change.oldDoc.createdAt) : null;

          // If never used and created within the last week, mark as notUsed
          if (usage?.useCount === 0 && oldDocCreatedAt && oldDocCreatedAt >= oneWeekAgo) {
            newResolutions[change._id] = { notUsed: true };
          }
        }
      }

      return newResolutions;
    });
  }, [pendingChanges]);

  const metadataChangeCount = useMemo(() => {
    return (pendingChanges || []).filter(c => c.metadataChange).length;
  }, [pendingChanges]);

  const filteredGroupedChanges = useMemo(() => {
    if (!hideMetadataChanges) return groupedChanges;
    return groupedChanges
      .map(([groupKey, keyGroupChanges]) => {
        const filtered = keyGroupChanges.filter(c => !c.metadataChange);
        return filtered.length > 0 ? [groupKey, filtered] : null;
      })
      .filter(Boolean);
  }, [groupedChanges, hideMetadataChanges]);

  const visibleGroupedChanges = useMemo(() => {
    return filteredGroupedChanges.slice(0, visibleModelLimit);
  }, [filteredGroupedChanges, visibleModelLimit]);

  const hiddenModelCount = filteredGroupedChanges.length - visibleGroupedChanges.length;

  const groupCommitErrors = useMemo(() => {
    const errors = {};
    for (const [groupKey, keyGroupChanges] of groupedChanges) {
      errors[groupKey] = canCommit(keyGroupChanges);
    }
    return errors;
  }, [groupedChanges, resolutions]);

  const keysToCommit = useMemo(() => {
    return groupedChanges
      .filter(([groupKey]) => checkedGroupKeys.has(groupKey) && !groupCommitErrors[groupKey])
      .map(([groupKey]) => groupKey);
  }, [groupCommitErrors, groupedChanges, checkedGroupKeys]);

  const changesToCommit = useMemo(() => {
    return (pendingChanges || []).filter(c => keysToCommit.includes(localeKey(c)));
  }, [keysToCommit, pendingChanges]);

  const commitError = useMemo(() => {
    if (keysToCommit.length > 0) {
      return null;
    }
    const firstError = _.find(_.map(groupedChanges, ([groupKey]) => checkedGroupKeys.has(groupKey) && groupCommitErrors[groupKey]), Boolean);
    return firstError || 'No valid key group to commit';
  }, [groupCommitErrors, groupedChanges, keysToCommit.length, checkedGroupKeys]);

  const commitChanges = async () => {
    const currentUserId = page.getLoggedUserSignature()?.id;

    // Check which keys have changes not from the current user
    const keysWithOtherUsers = [];
    for (const [groupKey, keyGroupChanges] of groupedChanges) {
      if (keysToCommit.includes(groupKey)) {
        const hasOtherUsers = keyGroupChanges.some(change => change.createdBy?.id !== currentUserId);
        if (hasOtherUsers) {
          keysWithOtherUsers.push(groupKey);
        }
      }
    }

    // Show confirmation if there are keys with changes from other users
    if (keysWithOtherUsers.length > 0) {
      const keyList = keysWithOtherUsers.map(localeKey => {
        const [locale, key] = localeKey.split('_');
        return `  • ${locale}: ${key}`;
      }).join('\n');

      const confirmed = window.confirm(
        `You are about to commit changes that include modifications from other users in:\n` +
        `${keyList}\n\n` +
        `Do you want to proceed?`
      );

      if (!confirmed) {
        return;
      }
    }

    page.runAsync(async () => {
      await stableUrlsService.create({ type, resolutions: expandResolutionsByCountries(changesToCommit, resolutions), localeKeys: keysToCommit });
      await fetchPendingChanges();
    }, 'Committing changes...');
  };

  const updateChanges = async (keys) => {
    await stableUrlsService.patch('', { type: 'fusebox', keys });
  }

  useEffect(() => {
    const handler = (changes) => {
      if (changes.some(c => c.type === type)) {
        fetchPendingChanges();
      }
    };
    stableUrlsService.on('urls-pending-changes', handler);

    fetchPendingChanges();

    return () => stableUrlsService.removeListener('urls-pending-changes', handler);
  }, [type, JSON.stringify(ungroupedKeys)]);


  if(!pendingChanges) {
    return <div className="text-center p-4 h3">Loading pending changes...</div>;
  }

  return <div>
    {pendingChanges.length ?
      <div className={"modal-footer mb-2 " + (commitError ? 'bg-light-danger' : 'bg-light-success')}>
        {commitError ?
          <div className="text-danger">{commitError}</div>
          :
          <div className="text-success">✅ Ready to commit {keysToCommit.map(localeKey => {
            const [locale, key] = localeKey.split('_');
            return <><LocaleFlag locale={locale} /><TrimSpec spec="modelId" className="mr-1" value={key} key={key} /></>;
          })} ({changesToCommit.length} changes)</div>
        }
        <button className={`btn btn-${commitError ? 'secondary' : 'primary'}`} disabled={!!commitError} onClick={commitChanges}>Commit Changes</button>
      </div>
      :
      <div className="bg-light-secondary p-3 text-center">No changes to commit</div>
    }

    {pendingChanges.length > 0 && (
      <div className="d-flex align-items-center mb-2 px-2 flex-gap">
        {metadataChangeCount > 0 && <>
          <SwitchInput value={hideMetadataChanges} onChange={setHideMetadataChanges}>
            Hide metadata changes ({metadataChangeCount})
          </SwitchInput>
          <IconButton icon="check_box" level="primary" onClick={() => setCheckedGroupKeys(new Set(groupedChanges.map(([groupKey]) => groupKey)))}>Check all</IconButton>
        </>}
        <IconButton icon="done_all" level="success" onClick={() => setResolutions(prev => {
          const next = { ...prev };
          for (const [, cs] of groupedChanges) for (const c of cs) {
            if (c.changeType !== 'remove' || next[c._id]?.notUsed) continue;
            const s = computeRemoveSuggestion(c, pendingChanges);
            if (s && !_.isEqual(next[c._id], s)) next[c._id] = s;
          }
          return next;
        })}>Accept all in all</IconButton>
      </div>
    )}

    <table className="table table-sm m-0">
      <thead>
        <tr className="bg-light">
          <th className="text-center"></th>
          <th className="text-center">✅</th>
          <th>Countries</th>
          <th>Change</th>
          <th>URL</th>
          <th>Suggestion</th>
          <th>Old Id- New Id</th>          
          <th>Resolution</th>
          <th>Total uses</th>
          <th>Last used</th>
        </tr>
      </thead>
      <tbody>
        {_.map(visibleGroupedChanges, ([groupKey, keyGroupChanges]) => {
          const [locale, key] = groupKey.split('_');
          const groupError = groupCommitErrors[groupKey];
          const isChecked = checkedGroupKeys.has(groupKey);
          const toggleChecked = () => {
            setCheckedGroupKeys(prev => {
              const newSet = new Set(prev);
              if (newSet.has(groupKey)) {
                newSet.delete(groupKey);
              } else {
                newSet.add(groupKey);
              }
              return newSet;
            });
          };

          const pendingGroupSuggestions = _.compact(keyGroupChanges.map(c => {
            if (c.changeType !== 'remove' || resolutions[c._id]?.notUsed) return null;
            const suggestion = computeRemoveSuggestion(c, pendingChanges);
            if (!suggestion || _.isEqual(resolutions[c._id], suggestion)) return null;
            return { _id: c._id, suggestion };
          }));

          const acceptAllSuggestions = () => setResolutions(prev => {
            const next = { ...prev };
            for (const { _id, suggestion } of pendingGroupSuggestions) next[_id] = suggestion;
            return next;
          });

          return <React.Fragment key={groupKey}>
            <tr className={`${groupError ? "bg-dark" : "bg-success text-white"} ${!isChecked ? 'translucent' : ''}`}>
              <td colSpan={11} className="text-center">
                <IconButton
                  icon={isChecked ? 'check_box' : 'check_box_outline_blank'}
                  level={isChecked ? 'light' : 'secondary'}
                  onClick={toggleChecked}
                  className="mr-2"
                />
                <LocaleFlag locale={locale} className="mr-2" />
                <TrimSpec spec="modelId" value={key} />

                <IconButton icon="sync" small onClick={() => updateChanges([key])} />

                {pendingGroupSuggestions.length > 0 ? <IconButton icon="done_all" level="success" className="ml-4" small onClick={acceptAllSuggestions}>Accept all suggestions ({pendingGroupSuggestions.length})</IconButton> : null}

                  {!ungroupedKeys.includes(key) ? <IconButton icon="link_off" level="secondary" className="ml-4" small onClick={() => setUngroupedKeys(prev => [...prev, key])}>Ungroup countries</IconButton> : null}
              </td>
            </tr>

            {_.sortBy(keyGroupChanges, c => (c.doc || c.oldDoc).url).map((change, i) => {
              const { changeType, ignore, _id, createdBy, doc, oldDoc, countries, metadataChange } = change;
              const res = resolutions[_id];
              const ready = changeIsReady(change);
              const suggestion = renderSuggestionAndWarnings(change, pendingChanges, resolutions, (newRes) => setResolutions(newRes));
              const usage = oldDoc?.usage;

              
                let metadata = doc?.metadata;                
                let viaInfo = null;
                if(metadata) {
                  let _modelId = metadata?.context?._modelId;
                  let _fromFamilyModelId = metadata?.context?._fromFamilyModelId;
                  viaInfo = <span className="text-secondary small mr-1 no-wrap">
                    {_modelId && _modelId !== metadata?.context?.modelId ? <>Via @Gen <TrimSpec className={'bg-secondary'} spec="modelId" value={_modelId} /></> : null}
                    {_fromFamilyModelId ? <>Via Family <TrimSpec spec="modelId" value={_fromFamilyModelId} /></> : null}
                  </span>;
                }


              return <tr key={_id} className={`${ready ? (groupError ? 'bg-light' : 'bg-light-success') : 'bg-light-warning'}${(ignore || metadataChange || !isChecked) ? ' translucent' : ''}`}>
                <td className="text-center py-0 align-middle">
                  {createdBy ? <UserAvatarLive user={createdBy} /> : '-'}
                </td>

                <td className="text-center py-0 align-middle">
                  {ready ? '✅' : '❌'}
                </td>

                <td className="text-center py-0 align-middle">
                  {countries.map(country => <CountryFlag key={country} countryCode={country} />)}
                </td>

                <td className="text-center py-0 align-middle">
                  <span className={"badge badge-" + (changeType === 'remove' ? 'danger' : changeType === 'update' ? 'warning' : 'success')}>{changeType.toUpperCase()}</span>
                </td>

                <td className="monospace text-info  py-0 align-middle text-ellipsis" style={{maxWidth: '250px'}} title={(doc || oldDoc).url}>
                  {(doc || oldDoc).url}
                </td>

                <td className=" py-0 align-middle">
                  {suggestion} { viaInfo }                  
                </td>

                <td className={"monospace text-secondary py-0 align-middle text-center small no-wrap "}>
                  <HighlightedId id={oldDoc?.id || doc?.id} />
                </td>

                <td className="py-0 align-middle" style={{ width: '360px' }}>                                 
                  {(oldDoc && doc && oldDoc.id !== doc.id) ? <>
                    <span className="text-secondary">⟶</span>
                    <HighlightedId id={doc?.id} />
                  </> : null}
                                    
                  <ResolutionEditor resolution={resolutions[_id]} onChange={(resolution) => setResolutions(prev => ({ ...prev, [_id]: resolution }))} change={change} />                  
                </td>

                <td className="py-0 align-middle text-right small">
                  {usage?.useCount != null ? (usage.useCount.toLocaleString()) : '-'}
                </td>

                <td className="py-0 align-middle small text-secondary no-wrap">
                  {usage?.lastUsedAt ? fromNow(new Date(usage.lastUsedAt)) : ''}
                </td>
              </tr>;
            })}
          </React.Fragment>
        })}
      </tbody>
    </table>

    {hiddenModelCount > 0 && (
      <div className="text-center my-3">
        <IconButton icon="expand_more" level="primary" onClick={() => setVisibleModelLimit(prev => prev + 10)}>
          Show more models ({hiddenModelCount} hidden)
        </IconButton>
        {hiddenModelCount > 10 && (
          <IconButton icon="unfold_more" level="secondary" className="ml-2" onClick={() => setVisibleModelLimit(filteredGroupedChanges.length)}>
            Show all
          </IconButton>
        )}
      </div>
    )}

  </div>;
}
